import { Fragment, useId, type ReactNode } from 'react';
import { Alert, Link, Skeleton, Text } from '@backstage/ui';
import { useRouteRef, useTranslationRef } from '@backstage/frontend-plugin-api';
import type { FieldValues } from '@internal/plugin-waadoo-config-common';
import { waadooIcons } from '../../ui/icons/waadooIcons';
import { Workbench, WorkbenchMain } from '../../waadooForm';
import {
  branchKind,
  branchTier,
  compareBranches,
  isEphemeralBranch,
} from '../branchKind';
import { branchRouteRef } from '../branchRouteRef';
import { TAB_QUERY_PARAM } from '../branchTabs';
import {
  useEnvironmentConfiguration,
  type ConfigurationResponse,
  type ConfigurationState,
} from '../configuration';
import {
  usePermanentEnvironments,
  type PermanentEnvironment,
} from '../permanentEnvironments';
import { projectEnvironmentsTranslationRef } from '../translation';

const LockedIcon = waadooIcons.locked;
const SleepingIcon = waadooIcons.sleeping;

/**
 * The project's environments on the left (layout « C3 »): its permanent
 * branches in the order of every list, each in its tier's colour, asleep
 * (the moon) or locked for the caller (the lock) — the production for an
 * editor, everything for a plain member, an ephemeral branch for everyone,
 * as the route's `lock` decides —, the one being read highlighted. Each
 * opens its own branch page on this tab. An ephemeral branch being read is
 * listed too, after dev.
 *
 * `access` is the caller's level on the project, from the route's answer:
 * no lock is drawn without it.
 */
function EnvironmentList(props: {
  project: string;
  current: string;
  permanent: readonly PermanentEnvironment[] | undefined;
  access?: ConfigurationResponse['access'];
}) {
  const { project, current, permanent, access } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const branchRoute = useRouteRef(branchRouteRef);
  const headingId = useId();

  const asleep = new Set(
    (permanent ?? []).filter(env => !env.active).map(env => env.name),
  );
  const names = (permanent ?? []).map(env => env.name);
  const entries = (names.includes(current) ? names : [...names, current]).sort(
    compareBranches,
  );
  const lockedFor = (branch: string) =>
    access !== undefined &&
    (isEphemeralBranch(branch) ||
      access === 'read' ||
      (branchKind(branch) === 'production' && access !== 'admin'));
  const href = (branch: string) =>
    branchRoute
      ? `${branchRoute({ project, branch })}?${new URLSearchParams({
          [TAB_QUERY_PARAM]: 'configuration',
        })}`
      : undefined;

  return (
    <nav className="waadoo-config-envs" aria-labelledby={headingId}>
      <Text
        as="h3"
        variant="body-small"
        weight="bold"
        color="secondary"
        className="waadoo-config-envs__title"
        id={headingId}
      >
        {t('configuration.environments')}
      </Text>
      <ul className="waadoo-config-envs__list">
        {entries.map(branch => {
          const link = href(branch);
          const isCurrent = branch === current;
          const marks = (
            <>
              <span className="waadoo-config-envs__name">{branch}</span>
              {asleep.has(branch) && (
                <span className="waadoo-config-envs__mark">
                  <SleepingIcon aria-hidden focusable="false" />
                  <span className="waadoo-visually-hidden">
                    , {t('sleeping.badge')}
                  </span>
                </span>
              )}
              {lockedFor(branch) && (
                <span className="waadoo-config-envs__mark">
                  <LockedIcon aria-hidden focusable="false" />
                  <span className="waadoo-visually-hidden">
                    , {t('configuration.environmentLocked')}
                  </span>
                </span>
              )}
            </>
          );
          // The tier's left edge, not an environment tag: a navigation
          // list, whose current item is already tinted in its tier's colours
          // (`.waadoo-config-envs__item[aria-current]`, styles.css) — a tag
          // inside would be a second tinted block.
          return (
            <li key={branch}>
              {link ? (
                <Link
                  href={link}
                  className="waadoo-config-envs__item"
                  data-tier={branchTier(branch)}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {marks}
                </Link>
              ) : (
                <span
                  className="waadoo-config-envs__item"
                  data-tier={branchTier(branch)}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {marks}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <Text
        as="p"
        variant="body-small"
        color="secondary"
        className="waadoo-config-envs__hint"
      >
        {t('configuration.environmentsHint')}
      </Text>
    </nav>
  );
}

/** What a type's editor of the configuration is given, once it is readable. */
export interface ConfigurationEditorInput<C extends FieldValues> {
  answer: ConfigurationResponse<C>;
  /** The environment's configuration (`answer.configuration`). */
  current: C;
  /** The project's permanent environments' names, as read so far. */
  permanentNames: string[];
}

/**
 * The frame of the « Configuration » tab (mockup « D », 2026-10-16; layout
 * « U1 », 2026-10-16, after « C3 »), the same for every type: three columns
 * (`Workbench`, components/waadooForm, as the creation form) — the
 * project's environments, each opening its own tab; then, once the route
 * answered, the type's editor (`renderEditor`: its `WorkbenchMain` and
 * `WorkbenchAside`), or what the route says instead — loading, not offered
 * here, failed, or values that cannot be read. A new answer (Refresh,
 * another branch) remounts the editor: it starts a new draft.
 */
export function ConfigurationWorkbench<
  C extends FieldValues = FieldValues,
>(props: {
  project: string;
  branch: string;
  reloadSignal?: number;
  renderEditor: (input: ConfigurationEditorInput<C>) => ReactNode;
}) {
  const { project, branch, reloadSignal, renderEditor } = props;
  const state = useEnvironmentConfiguration<C>(project, branch, reloadSignal);
  const permanent = usePermanentEnvironments(project, reloadSignal);

  return (
    <div className="waadoo-config">
      <Workbench
        nav={
          <EnvironmentList
            project={project}
            current={branch}
            permanent={permanent}
            access={state.status === 'ready' ? state.answer.access : undefined}
          />
        }
      >
        <ConfigurationContent
          state={state}
          permanent={permanent}
          renderEditor={renderEditor}
        />
      </Workbench>
    </div>
  );
}

function ConfigurationContent<C extends FieldValues>(props: {
  state: ConfigurationState<C>;
  permanent: readonly PermanentEnvironment[] | undefined;
  renderEditor: (input: ConfigurationEditorInput<C>) => ReactNode;
}) {
  const { state, permanent, renderEditor } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);

  if (state.status === 'loading') {
    return (
      <WorkbenchMain>
        <Skeleton />
      </WorkbenchMain>
    );
  }
  if (state.status === 'unavailable') {
    return (
      <WorkbenchMain>
        <Text as="p" variant="body-medium" color="secondary">
          {t('configuration.unavailable')}
        </Text>
      </WorkbenchMain>
    );
  }
  if (state.status === 'failed') {
    return (
      <WorkbenchMain>
        <Text as="p" variant="body-medium" color="danger">
          {t('configuration.failed')}
        </Text>
      </WorkbenchMain>
    );
  }
  const { answer } = state;
  if (!answer.configuration) {
    return (
      <WorkbenchMain>
        <Alert
          status="warning"
          icon
          title={t('configuration.unreadableTitle', {
            files: answer.files.join(', '),
          })}
          description={
            <>
              <Text as="p" variant="body-small">
                {t('configuration.unreadableDescription')}
              </Text>
              <ul>
                {answer.problems.map(problem => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </>
          }
        />
      </WorkbenchMain>
    );
  }
  return (
    // A new answer (Refresh, another branch) starts a new draft.
    <Fragment key={JSON.stringify(answer.configuration) + answer.environment}>
      {renderEditor({
        answer,
        current: answer.configuration,
        permanentNames: (permanent ?? []).map(env => env.name),
      })}
    </Fragment>
  );
}
